import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { isTauri } from '@/tauri/utils';
import type { DesktopConnectivity } from '@/types/offline-recording';
import { logger } from '@/utils/logger/logger';
import { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getDefaultStore } from 'jotai';

/**
 * How long a confirmed-online reading stays discredited after an outbound
 * request came back with nothing.
 *
 * Sized from the worst case in `src-tauri/src/offline_recording/connectivity.rs`
 * for turning a re-probe into a published transition — a blackholed link, where
 * every probe burns its full timeout instead of failing fast. One evaluation is
 * `BACKEND_PROBE_TIMEOUT` 4s plus a TCP sweep of both `INTERNET_PROBE_HOSTS` at
 * `INTERNET_PROBE_TIMEOUT` 3s each (10s), and the first degraded sample is HELD
 * for confirmation rather than published, so a second evaluation follows after
 * `CONFIRMATION_PROBE_DELAY` 1.5s — 21.5s, plus IPC slack.
 *
 * `BASE_PROBE_INTERVAL` (10s) is deliberately NOT in the budget: arming also
 * wakes the monitor for an immediate probe, collapsing that wait to zero.
 */
const CONNECTIVITY_RECHECK_WINDOW_MS = 25_000;

/**
 * How long after the WebView loads a still-unsettled reading is allowed to
 * silence a raw transport-error toast.
 *
 * This is the COLD-BOOT race, and it is a different problem from the discredit
 * window above. At launch the connectivity atom starts at `unknown`, which the
 * shared snapshot maps to `pending`, and the app's mount-time queries fire
 * before the Rust monitor has published anything at all. On macOS WKWebView
 * pins `navigator.onLine` to `true` even with the link down, so the browser
 * fast-path never fires either. For that stretch the app genuinely does not
 * know whether it is reachable, and every failed boot query raises a red toast.
 * Telling a clinician "Network Error" while we cannot tell is worse than saying
 * nothing for a few seconds.
 *
 * Sized to match `DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS` in
 * `src/hooks/desktop/useDesktopConnectivity.ts` — the app already has a ruling
 * on how long boot may wait for connectivity to answer before giving up on it,
 * and this is the same wait seen from the XHR layer. Kept as its own constant
 * rather than imported so the http layer does not pull in a React hook module;
 * if that ceiling moves, move this with it.
 *
 * It MUST stay bounded, and the bound is the whole reason this is safe. `unknown`
 * is not a boot-only state: it is the permanent steady state on every client with
 * the offline-recording flag off, because nothing arms the probe there. Treating
 * `unknown` as offline-enough with no time limit would silence genuine online
 * transport errors for most of the fleet forever, which is exactly why the
 * guard was originally written to refuse it. Bounding it to the launch window
 * keeps that refusal everywhere except the one window where the app has had no
 * chance to know.
 */
const UNSETTLED_BOOT_GRACE_MS = 8_000;

/**
 * When this WebView loaded, i.e. when the app launched.
 *
 * Module-init is a sound stand-in for app start on desktop: the WebView is
 * created once per launch and the app has no reload path (a reload does nothing
 * there), so this is evaluated exactly once per cold boot.
 */
let bootGraceStartedAt = Date.now();

/**
 * How long after a window closes before another may be armed.
 *
 * This stops a broken endpoint on a healthy network from suppressing its own
 * toasts indefinitely: each failure would otherwise re-arm the next. Suppression
 * becomes periodic rather than permanent, so an ongoing genuine failure is heard
 * for the great majority of every cycle.
 *
 * Must stay a cooldown, never a once-ever latch. A latch is spent by the first
 * transient failure while truly online, and hours later a real link drop lands on
 * a still-stale `online_ready_to_sync` with nothing left to arm — the original
 * bug, back, after an entirely ordinary precondition.
 *
 * Residual gap, accepted: arming is once per cooldown, so an unrelated online
 * failure burns the arm, and a genuine drop later in that same cooldown gets no
 * window and still toasts. That is the safe direction to be wrong in — it shows
 * an error that could have been hidden, never hides one that should have shown.
 *
 * A timer is needed here only because the reading carries no age. Rust publishes
 * on a state CHANGE (`should_publish` / `publish` in
 * `src-tauri/src/offline_recording/connectivity.rs`), so the atom's write time is
 * the last transition and can be hours old. Returning a probe timestamp from the
 * `offline_connectivity_status` command replaces both timers with a fact and
 * deletes this constant.
 */
const CONNECTIVITY_RECHECK_COOLDOWN_MS = 5 * 60_000;

/**
 * When the current discredit window expires, and the earliest another may be
 * armed. Both `0` when nothing is in play.
 *
 * Module-level rather than atom-level because this is a property of the XHR
 * layer's own observations, not shared app state: nothing renders from it, and
 * the axios interceptors that read it are not components.
 */
let recheckArmedUntil = 0;
let recheckCooldownUntil = 0;

/**
 * Datadog tags for the discredit window's four transitions.
 *
 * The Rust half already logs its own confirmation holds, so without these only
 * half the story reaches Datadog and two questions stay unanswerable after ship:
 * how often a window closes on a real probe answer (the fix working) rather than
 * on its timer (an error we hid), and how often the cooldown skips an arm (the
 * accepted residual gap actually biting).
 *
 * `skipped_cooldown` counts only failures with no open window, never repeats
 * inside one — see {@link armConnectivityRecheck}. Widening it would make the
 * second of those two numbers unreadable.
 *
 * Deliberately tags and enums only. The arming path holds the URL, the request
 * body and the error message; none of it is passed here, and none of it should
 * be added.
 */
const TAG_ARMED = 'tag="connectivity_discredit_armed"';
const TAG_SKIPPED_COOLDOWN = 'tag="connectivity_discredit_skipped_cooldown"';
const TAG_CLOSED = 'tag="connectivity_discredit_closed"';
const TAG_PROBE_NUDGE_FAILED = 'tag="connectivity_probe_nudge_failed"';

/**
 * Datadog tag for a toast silenced by the cold-boot grace period.
 *
 * This is the one suppression path that fires on an ABSENCE of evidence rather
 * than on a reading, so it is the one whose blast radius has to be measurable
 * after ship: how many toasts it eats, and whether any of them keep arriving
 * from clients that turned out to be online. Tag only — no URL, no status, no
 * error message.
 */
const TAG_BOOT_GRACE_SUPPRESSED = 'tag="connectivity_boot_grace_suppressed"';

// Axios's code for a request the app itself aborted (an `AbortSignal` on a
// query, a component unmounting). Not a reachability signal.
const AXIOS_CANCELED_ERROR_CODE = 'ERR_CANCELED';

/**
 * Ask the Rust monitor to probe now rather than at its next scheduled tick.
 *
 * Dynamically imported so the Tauri event module stays out of the axios
 * interceptor's module graph until a request has actually failed. Best-effort:
 * if the bridge is unavailable the discredit window still holds, it just runs
 * to its full length instead of being cut short by the probe's answer.
 */
function requestImmediateConnectivityProbe(): void {
  void import('@/tauri/offline-recording/connectivity')
    .then(({ connectivityBridge }) => connectivityBridge.notifyNetworkChange())
    .catch(() => {
      // Not rethrown — the caller is an error path that must not raise a second
      // error of its own — but not silent either: the window has already
      // committed to suppressing for its full length on the assumption this
      // probe would answer, and without this line a failed nudge is invisible.
      logger.warn(`[connectivity] ${TAG_PROBE_NUDGE_FAILED}`);
    });
}

/**
 * Single interpretation of desktop reachability for the outbound-XHR layer,
 * shared by both predicates below so the `unknown` state is read one way, not
 * two divergent ways. Non-React (reads the atom via `getDefaultStore`) because
 * it runs inside axios interceptors / auth restore, not a component.
 *
 * Precedence differs on purpose from the UI `useDesktopConnectivity` hook: for
 * "will this XHR fail?", a browser-offline signal is authoritative — a dropped
 * link fails the request even if the last Rust probe said online — whereas the
 * UI hook lets a confirmed native `online` win over a lying WKWebView. `unknown`
 * maps to `pending` (reachability not yet settled).
 *
 * The one addition is the discredit window. `online_ready_to_sync` is the last
 * *published* probe result and the monitor publishes only on a change, so on
 * macOS — where WKWebView keeps `navigator.onLine` pinned `true` and the browser
 * fast-path never fires — it can outlive the connection it describes by a full
 * probe cycle. While a window armed by {@link armConnectivityRecheck} is open the
 * reading degrades to `pending`. Windows never reaches this: WebView2 flips
 * `navigator.onLine` promptly and the fast-path answers first.
 *
 * Pure: it reads the window but never expires or clears it. Advancing that state
 * is {@link observeConnectivityForRecheck}'s job, so the window's lifetime is a
 * property of the two XHR-layer predicates rather than of whoever happens to ask
 * a question.
 *
 * The window is not scoped to the toast path. Degrading to `pending` flips
 * {@link isDesktopOfflineNetworkMode} for its length, and that predicate is also
 * one of the gates in `getOfflineAuth0DesktopSession`
 * (`src/tauri/auth0/provider.tsx`) that resurrect a session from a persisted
 * token. So an open window makes that gate more permissive for 25s. It is the
 * weakest of the gates there — the real filter is `!isAuthenticated &&
 * hasRefreshToken` plus a persisted token — and the predicate is already
 * permanently `true` wherever the atom sits at `unknown`, which is every client
 * with the offline-recording flag off. The window widens a condition that is
 * already true for most of the fleet; it does not open a new one. Note also the
 * standing warning in `src/lib/auth/readFromProviderSourceOfTruth.ts` against
 * reusing this predicate: it is deliberately pessimistic, and more so now.
 */
function getDesktopNetworkConnectivity(): DesktopConnectivity {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';

  const connectivity = getDefaultStore().get(offlineConnectivityStateAtom);
  if (connectivity === 'offline' || connectivity === 'backend_unreachable')
    return 'offline';
  if (connectivity !== 'online_ready_to_sync') return 'pending';

  return recheckArmedUntil !== 0 && Date.now() < recheckArmedUntil ? 'pending' : 'online';
}

/** Whether the last published reading still says the backend is reachable. */
function isConfirmedOnlineReading(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  return getDefaultStore().get(offlineConnectivityStateAtom) === 'online_ready_to_sync';
}

/**
 * Record that the XHR layer has just looked at the network, and advance the
 * window accordingly. The single owner of that state's lifetime.
 *
 * Two transitions. A window whose time is up lapses, so the next read sees
 * `online` again and the cooldown starts running. And a reading that has stopped
 * being confirmed-online voids the whole stretch, cooldown included — a
 * drop-and-reconnect cycle is exactly when the next arm matters most, and making
 * it wait out a cooldown earned before the reconnect would put the second outage
 * back in the dark.
 *
 * Called from the two exported predicates rather than from the shared read,
 * because those are what "the XHR layer looked at the network" actually means;
 * `getDesktopNetworkConnectivity` is also reached from arming, and from anything
 * that later reuses it, none of which should move policy state.
 */
function observeConnectivityForRecheck(): void {
  if (!isConfirmedOnlineReading()) {
    if (recheckArmedUntil !== 0 && Date.now() < recheckArmedUntil) {
      // The reading left `online_ready_to_sync` while the window was still open,
      // so the probe published a degraded state and the honest reading has taken
      // over. This is the fix working, and the counterpart to `reason="timeout"`.
      logger.info(`[connectivity] discredit window closed ${TAG_CLOSED} reason="probe"`);
    }
    recheckArmedUntil = 0;
    recheckCooldownUntil = 0;
    return;
  }

  if (recheckArmedUntil !== 0 && Date.now() >= recheckArmedUntil) {
    recheckArmedUntil = 0;
    // The atom never left `online_ready_to_sync`, so no degraded state was ever
    // published: the probe found the backend reachable and the window ran to its
    // timer. That is the case where a real error was hidden for 25s.
    logger.info(`[connectivity] discredit window closed ${TAG_CLOSED} reason="timeout"`);
  }
}

/**
 * Treat a request that came back with nothing as evidence against the last
 * published probe.
 *
 * Called only from the classifier below, and only for a no-response failure —
 * a request that got a real HTTP response never reaches here, so a genuine
 * server error keeps surfacing exactly as it did.
 *
 * A dropped link and a broken endpoint on a healthy network look identical from
 * inside the WebView; only the probe's answer separates them. If the link really
 * dropped the probe publishes a degraded state and the honest `offline` reading
 * takes over from the window; if it did not, nothing is published, the window
 * expires, and {@link CONNECTIVITY_RECHECK_COOLDOWN_MS} governs what happens next.
 */
function armConnectivityRecheck(error: Partial<AxiosError>): void {
  // A cancellation has the same shape as a failure — no response, a populated
  // `request` — but we aborted it ourselves, so it is not evidence about
  // reachability. Guarded here rather than in the shape predicate so that
  // classification stays byte-identical for every consumer; only arming changes.
  if (error.code === AXIOS_CANCELED_ERROR_CODE) return;

  const now = Date.now();
  if (now < recheckCooldownUntil) {
    // The cooldown is scheduled at arm time and spans the window as well as the
    // five minutes after it, so this branch also catches every repeat failure
    // while a window is OPEN — which, during a real drop, is most of them. Those
    // are not the residual gap: they are already being suppressed by the arm they
    // would have duplicated. Counting them would drown the one number this tag
    // exists to produce, so only a failure with no window left to shelter under
    // is logged.
    if (now >= recheckArmedUntil) {
      logger.info(`[connectivity] discredit window not armed ${TAG_SKIPPED_COOLDOWN}`);
    }
    return;
  }

  observeConnectivityForRecheck();
  // Only a reading that currently resolves to `online` can be discredited.
  // Notably this excludes the Windows path, where WebView2 has already flipped
  // `navigator.onLine` and the browser fast-path answers `offline` on its own —
  // so nothing about Windows behaviour, or the guard that keeps a WebView2
  // false-offline from winning, changes here. It equally excludes `unknown`,
  // which is the permanent state on every client with the offline-recording flag
  // off: no probe target, so nothing is ever published. That early return is what
  // keeps a flag-off fleet from nudging the probe on every failed request.
  if (getDesktopNetworkConnectivity() !== 'online') return;

  recheckArmedUntil = now + CONNECTIVITY_RECHECK_WINDOW_MS;
  recheckCooldownUntil = recheckArmedUntil + CONNECTIVITY_RECHECK_COOLDOWN_MS;
  logger.info(`[connectivity] discredit window armed ${TAG_ARMED}`);
  requestImmediateConnectivityProbe();
}

/**
 * Clear the discredit window and its cooldown. Test-only: the state is
 * module-level, so without this a case that arms it would leak into the next.
 */
export function resetDesktopOfflineRecheckStateForTests(): void {
  recheckArmedUntil = 0;
  recheckCooldownUntil = 0;
}

/**
 * Restart the cold-boot grace period from now. Test-only: the start point is
 * captured at module init, so without this every case inherits however much of
 * the window the surrounding suite has already burned, and a slow run would
 * flake.
 */
export function restartDesktopOfflineBootGraceForTests(): void {
  bootGraceStartedAt = Date.now();
}

export function isDesktopOfflineNetworkMode(): boolean {
  if (!isTauri()) return false;

  try {
    observeConnectivityForRecheck();
    // Anything other than a confirmed online reading (i.e. `offline` OR the
    // still-unsettled `pending`) counts as offline for error classification —
    // "unknown" is not safe to treat as reachable when deciding whether a
    // network error was expected.
    return getDesktopNetworkConnectivity() !== 'online';
  } catch {
    return false;
  }
}

export function isConfirmedDesktopOfflineNetworkMode(): boolean {
  if (!isTauri()) return false;

  try {
    observeConnectivityForRecheck();
    // Only a *confirmed* offline reading short-circuits outbound requests; a
    // still-unsettled `pending` must not preemptively block XHR.
    return getDesktopNetworkConnectivity() === 'offline';
  } catch {
    return false;
  }
}

// Axios's canonical message for a failed-to-reach-the-server error. Kept as a
// named constant so the string appears once, only as a last-resort fallback —
// the suppression net is keyed off the stable error code / no-response shape,
// not this English copy.
const AXIOS_NETWORK_ERROR_MESSAGE = 'Network Error';

/**
 * Whether the error is the "left the app, nothing came back" shape.
 *
 * A response means the server actually answered — that is a real error the user
 * should see, not an offline network failure, and it is rejected first so no
 * amount of connectivity doubt can hide it.
 *
 * A cancellation matches this shape too, and deliberately still does. Excluding
 * it here would change the exported classification for every consumer, so the
 * `ERR_CANCELED` guard lives in {@link armConnectivityRecheck} instead — the only
 * place that treats the failure as evidence about the network.
 *
 * Beyond that, key off stable, i18n-proof signals rather than the English
 * message: the axios error code, or the `request`-set/`response`-absent shape.
 * `message === 'Network Error'` stays only as a last-resort fallback for
 * non-axios error shapes that carry neither a code nor a request handle.
 */
function isNoResponseNetworkFailure(error: unknown): error is AxiosError {
  const axiosLikeError = error as Partial<AxiosError> | undefined;
  if (!axiosLikeError) return false;
  if (axiosLikeError.response) return false;

  return (
    axiosLikeError.code === 'ERR_NETWORK' ||
    axiosLikeError.code === 'ECONNABORTED' ||
    axiosLikeError.request != null ||
    axiosLikeError.message === AXIOS_NETWORK_ERROR_MESSAGE
  );
}

/**
 * Classify an error as a desktop offline network failure.
 *
 * **This predicate has side effects, by design.** On a no-response failure it can
 * open the discredit window and wake the Rust monitor for an out-of-band probe —
 * the failure is the evidence, so it has to be weighed as part of classifying it,
 * not before or after. Consumers that take this as an `isOfflineNetworkError`
 * port (`@heidi/workspace/data`) are handing it the same authority.
 *
 * Safe to call repeatedly: arming is idempotent within its cooldown, and a
 * responded error is rejected before any of it runs.
 */
export function isDesktopOfflineNetworkError(error: unknown): error is AxiosError {
  if (!isTauri()) return false;
  if (!isNoResponseNetworkFailure(error)) return false;

  try {
    // Order matters: the failure is itself the evidence, so it has to be weighed
    // BEFORE the reading it discredits is read. Checking the mode first would
    // return `false` on the stale `online_ready_to_sync` and never reach here —
    // which is the bug this exists to fix, not a refactor of it.
    armConnectivityRecheck(error as Partial<AxiosError>);
  } catch {
    // A store read that throws leaves the reading exactly as it was; the mode
    // check below has its own guard for the same failure.
  }

  return isDesktopOfflineNetworkMode();
}

/**
 * Whether a confirmed-online reading is currently under an open discredit
 * window — i.e. a request has just come back with nothing and the re-probe this
 * triggered has not yet had time to answer.
 */
function isDesktopOfflineRecheckArmed(): boolean {
  return recheckArmedUntil !== 0 && Date.now() < recheckArmedUntil;
}

/**
 * Whether we are still inside the cold-boot grace period AND connectivity has
 * not settled into a confirmed reading either way.
 *
 * Both halves are load-bearing. Once the monitor publishes anything the reading
 * stops being `pending` and this goes false immediately, however much of the
 * window is left — a confirmed answer always beats the grace period. And once
 * {@link UNSETTLED_BOOT_GRACE_MS} has elapsed this goes false for the rest of
 * the session, which is what keeps a permanently-`unknown` (flag-off) client
 * from being silenced forever.
 */
function isUnsettledDesktopBootWindow(): boolean {
  if (Date.now() - bootGraceStartedAt >= UNSETTLED_BOOT_GRACE_MS) return false;
  return getDesktopNetworkConnectivity() === 'pending';
}

/**
 * App-layer toast guard for call sites that still have the original Axios error.
 *
 * Use this before raising a generic network-failure toast. Three readings
 * suppress, and each is a different kind of claim:
 *
 * - **Confirmed offline** — the probe has said so. Suppress.
 * - **A discredited confirmed-online reading** — positive evidence (this request
 *   came back with nothing while the last published reading said online), so it
 *   suppresses for as long as the window is open. This is the macOS stale-reading
 *   case.
 * - **A still-unsettled reading inside the cold-boot grace period** — an absence
 *   of evidence rather than evidence, and the only one of the three that
 *   suppresses without a reading to stand on. It is admitted because at launch
 *   there is no reading to be had yet: the mount-time query burst outruns the
 *   monitor's first publish, and on macOS nothing else can tell. Bounded by
 *   {@link UNSETTLED_BOOT_GRACE_MS}, which is what stops it becoming the
 *   fleet-wide silence a plain "treat `unknown` as offline" rule would be.
 *
 * Never suppresses an error that carried an HTTP response: a real server 500
 * while online is rejected by {@link isDesktopOfflineNetworkError} before any of
 * this runs, in every reading. And this only ever silences the TOAST — callers
 * log first and guard second, so nothing is swallowed.
 *
 * The error check runs first because it is what arms the window it then reads.
 */
export function shouldSuppressDesktopOfflineNetworkErrorToast(error: unknown): boolean {
  if (!isDesktopOfflineNetworkError(error)) return false;
  if (isConfirmedDesktopOfflineNetworkMode() || isDesktopOfflineRecheckArmed()) {
    return true;
  }

  if (!isUnsettledDesktopBootWindow()) return false;

  logger.info(
    `[connectivity] network error toast suppressed while connectivity is unsettled at boot ${TAG_BOOT_GRACE_SUPPRESSED}`
  );
  return true;
}

function createDesktopOfflineRequestError(
  config: InternalAxiosRequestConfig
): AxiosError {
  return new AxiosError('Network Error', 'ERR_NETWORK', config);
}

export function attachConfirmedDesktopOfflineRequestShortCircuit(
  client: AxiosInstance
): void {
  client.interceptors.request.use((config) => {
    if (isConfirmedDesktopOfflineNetworkMode()) {
      return Promise.reject(createDesktopOfflineRequestError(config));
    }

    return config;
  });
}
